import { useState, useCallback } from "react";
import { useChips, useAgentChips, useSaveAgentChips, type Chip } from "../api/chips";

const AGENTS = [
  { id: "prime", label: "Prime Thread" },
  { id: "briefing", label: "Briefing" },
];

export function ChipsView() {
  const [agent, setAgent] = useState("prime");
  const [readerChip, setReaderChip] = useState<Chip | null>(null);
  const [pendingIds, setPendingIds] = useState<string[] | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  const allChips = useChips();
  const agentChips = useAgentChips(agent);
  const saveChips = useSaveAgentChips();

  const slottedIds = pendingIds ?? agentChips.data?.items.map((c) => c.id) ?? [];
  const library = allChips.data?.items ?? [];
  const slottedSet = new Set(slottedIds);
  const slottedChips = slottedIds
    .map((id) => library.find((c) => c.id === id))
    .filter((c): c is Chip => c !== undefined);
  const availableChips = library.filter((c) => !slottedSet.has(c.id) && !c.archived);

  const isDirty = pendingIds !== null;

  const handleSlot = useCallback((chipId: string) => {
    setPendingIds((prev) => [...(prev ?? slottedIds), chipId]);
  }, [slottedIds]);

  const handleUnslot = useCallback((chipId: string) => {
    setPendingIds((prev) => (prev ?? slottedIds).filter((id) => id !== chipId));
  }, [slottedIds]);

  const handleSave = useCallback(() => {
    if (!pendingIds) return;
    saveChips.mutate(
      { agent, chipIds: pendingIds },
      {
        onSuccess: () => setPendingIds(null),
      }
    );
  }, [agent, pendingIds, saveChips]);

  const handleDiscard = useCallback(() => {
    setPendingIds(null);
  }, []);

  const handleAgentSwitch = useCallback((newAgent: string) => {
    setAgent(newAgent);
    setPendingIds(null);
    setReaderChip(null);
  }, []);

  // Drag handlers
  const onDragStart = useCallback((chipId: string) => {
    setDragId(chipId);
  }, []);

  const onDragEnd = useCallback(() => {
    setDragId(null);
  }, []);

  const onDropToDeck = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (dragId && !slottedSet.has(dragId)) {
      handleSlot(dragId);
    }
    setDragId(null);
  }, [dragId, slottedSet, handleSlot]);

  const onDropToLibrary = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (dragId && slottedSet.has(dragId)) {
      handleUnslot(dragId);
    }
    setDragId(null);
  }, [dragId, slottedSet, handleUnslot]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  if (allChips.isLoading || agentChips.isLoading) {
    return <div className="chips-loading"><div className="spinner" /></div>;
  }

  return (
    <div className="chips-view">
      {/* Agent selector tabs */}
      <div className="chips-agent-tabs">
        {AGENTS.map((a) => (
          <button
            key={a.id}
            className={`chips-agent-tab ${agent === a.id ? "active" : ""}`}
            onClick={() => handleAgentSwitch(a.id)}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div className="chips-board">
        {/* Deck — slotted chips */}
        <div
          className={`chips-deck ${dragId && !slottedSet.has(dragId) ? "drop-target" : ""}`}
          onDragOver={onDragOver}
          onDrop={onDropToDeck}
        >
          <div className="chips-section-header">
            <h2>Deck</h2>
            <span className="chips-count">{slottedChips.length} slotted</span>
          </div>
          <p className="chips-section-desc">
            Chips plugged into {AGENTS.find((a) => a.id === agent)?.label}.
            Takes effect on next session.
          </p>
          <div className="chips-slot-area">
            {slottedChips.length === 0 && (
              <div className="chips-empty">No chips slotted. Drag chips here from the library.</div>
            )}
            {slottedChips.map((chip) => (
              <ChipCard
                key={chip.id}
                chip={chip}
                onInspect={() => setReaderChip(chip)}
                onAction={() => handleUnslot(chip.id)}
                actionLabel="Remove"
                draggable
                onDragStart={() => onDragStart(chip.id)}
                onDragEnd={onDragEnd}
              />
            ))}
          </div>
        </div>

        {/* Library — available chips */}
        <div
          className={`chips-library ${dragId && slottedSet.has(dragId) ? "drop-target" : ""}`}
          onDragOver={onDragOver}
          onDrop={onDropToLibrary}
        >
          <div className="chips-section-header">
            <h2>Library</h2>
            <span className="chips-count">{availableChips.length} available</span>
          </div>
          <p className="chips-section-desc">
            Available chips. Drag into the deck to activate.
          </p>
          <div className="chips-slot-area">
            {availableChips.length === 0 && (
              <div className="chips-empty">All chips are slotted.</div>
            )}
            {availableChips.map((chip) => (
              <ChipCard
                key={chip.id}
                chip={chip}
                onInspect={() => setReaderChip(chip)}
                onAction={() => handleSlot(chip.id)}
                actionLabel="Slot"
                draggable
                onDragStart={() => onDragStart(chip.id)}
                onDragEnd={onDragEnd}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Save / discard bar */}
      {isDirty && (
        <div className="chips-action-bar">
          <span className="chips-dirty-label">Unsaved changes</span>
          <button className="btn" onClick={handleDiscard}>Discard</button>
          <button
            className="btn chips-save-btn"
            onClick={handleSave}
            disabled={saveChips.isPending}
          >
            {saveChips.isPending ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      )}

      {/* Chip reader */}
      {readerChip && (
        <div className="chips-reader-overlay" onClick={() => setReaderChip(null)}>
          <div className="chips-reader" onClick={(e) => e.stopPropagation()}>
            <div className="chips-reader-header">
              <h3>{readerChip.name}</h3>
              <div className="chips-reader-meta">
                <span className={`chip-type-badge chip-type-${readerChip.type}`}>{readerChip.type}</span>
                <span className={`chip-emphasis-badge chip-emphasis-${readerChip.emphasis}`}>{readerChip.emphasis}</span>
                <span className="chip-author">by {readerChip.author}</span>
              </div>
              <button className="btn chips-reader-close" onClick={() => setReaderChip(null)}>Close</button>
            </div>
            <p className="chips-reader-desc">{readerChip.description}</p>
            <pre className="chips-reader-content">{readerChip.content}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

// --- ChipCard component ---

interface ChipCardProps {
  chip: Chip;
  onInspect: () => void;
  onAction: () => void;
  actionLabel: string;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

function ChipCard({ chip, onInspect, onAction, actionLabel, draggable, onDragStart, onDragEnd }: ChipCardProps) {
  return (
    <div
      className={`chip-card chip-type-border-${chip.type}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="chip-card-body" onClick={onInspect} title="Click to read full content">
        <div className="chip-card-name">{chip.name}</div>
        <div className="chip-card-badges">
          <span className={`chip-type-badge chip-type-${chip.type}`}>{chip.type}</span>
          <span className={`chip-emphasis-badge chip-emphasis-${chip.emphasis}`}>{chip.emphasis}</span>
        </div>
        <div className="chip-card-desc">{chip.description}</div>
      </div>
      <button className="chip-card-action" onClick={onAction}>{actionLabel}</button>
    </div>
  );
}
